import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, ImageOff } from "lucide-react";
import { PageHeader, Loader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, apiError, fileUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

function AdSlot({ ad, onSaved }) {
  const [file, setFile] = useState(null);
  const [enabled, setEnabled] = useState(ad.enabled);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const fd = new FormData();
    fd.append("enabled", enabled ? "true" : "false");
    if (file) fd.append("image", file);
    try {
      await api.put(`/admin/ads/${ad.slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Ad ${ad.slot} saved`); setFile(null); onSaved();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const preview = file ? URL.createObjectURL(file) : (ad.image ? fileUrl(ad.image) : null);

  return (
    <div className="ec-card p-5" data-testid={`ad-slot-${ad.slot}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-head font-semibold text-white">Ad {ad.slot}</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{enabled ? "Enabled" : "Disabled"}</span>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid={`ad-enabled-${ad.slot}`} />
        </div>
      </div>
      <div className="aspect-video rounded-xl border border-white/10 bg-black/40 overflow-hidden flex items-center justify-center mb-4">
        {preview ? <img src={preview} alt={`Ad ${ad.slot}`} className="w-full h-full object-contain" /> : <ImageOff className="w-10 h-10 text-gray-600" />}
      </div>
      <label className="flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-white/15 bg-white/5 cursor-pointer hover:border-orange-500/50 transition-colors mb-3">
        <Upload className="w-5 h-5 text-orange-400" />
        <span className="text-sm text-gray-400 truncate">{file?.name || "Upload / replace from Gallery / Device"}</span>
        <input type="file" accept="image/*" className="hidden" data-testid={`ad-file-${ad.slot}`} onChange={(e) => setFile(e.target.files[0])} />
      </label>
      <Button onClick={save} disabled={busy} data-testid={`ad-save-${ad.slot}`} className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold">{busy ? "..." : "Save"}</Button>
    </div>
  );
}

export default function AdminAds() {
  const { t } = useI18n();
  const [ads, setAds] = useState(null);
  const load = () => api.get("/admin/ads").then(({ data }) => setAds(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!ads) return <Loader />;

  return (
    <div>
      <PageHeader title={t("ads_mgmt")} subtitle="Upload up to 3 ads shown to members after login. Disabled or empty slots are skipped." />
      <div className="grid md:grid-cols-3 gap-6">
        {ads.map((ad) => <AdSlot key={ad.slot} ad={ad} onSaved={load} />)}
      </div>
    </div>
  );
}
